import { GameWithRels } from '@statsbomb/parachute-types';
import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { atomWithSuspenseQuery } from 'jotai-tanstack-query';
import { comparisonFiltersAtom } from '@/atoms/filters/comparison';
import {
  comparisonTeamCompetitionIdsToFilterByAtom,
  comparisonTeamCompetitionOptionsAtom,
  comparisonTeamSeasonIdsToFilterByAtom,
  comparisonTeamSeasonOptionsAtom,
} from '@/atoms/filters/team/teamComparisonFilters';
import { comparisonIdAtom } from '@/atoms/queryParams';
import { FormattedGame, FormattedGameWithTeam } from '@/types/game';
import { getFilteredGames, getGamesBySeason, getSelectedGames } from '@/utils/atoms/game';
import {
  filterGamesByCompetition,
  filterGamesByGameType,
  filterGamesBySeason,
  pipeFilterFunctions,
} from '@/utils/filters';
import { convertToDropdownOption } from '@/utils/object';
import { objSnakeToCamel } from '@/utils/queries';
import { fetchClientAtom } from '../client';

const rawTeamComparisonGamesAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['comparisonTeamGames', get(comparisonIdAtom)] as const;

  const queryFn = async ({ queryKey: [, comparisonId] }: { queryKey: typeof queryKey }) => {
    if (!comparisonId) return [];
    return (await fetch(`/team/${comparisonId}/games`)) as GameWithRels[];
  };

  return { queryKey, queryFn };
});

const teamComparisonGamesAtom = atom(async get => {
  const comparisonId = get(comparisonIdAtom);
  const rawTeamComparisonGames = await get(rawTeamComparisonGamesAtom);
  return rawTeamComparisonGames.data.map(game => ({ ...objSnakeToCamel(game), team: { teamId: comparisonId } }));
});

const comparisonTeamGameFilterFunctionsAtom = atom(get => {
  const selectedGameType = get(comparisonFiltersAtom).gameType.value;
  const selectedCompetitionIds = get(comparisonTeamCompetitionIdsToFilterByAtom);
  const selectedSeasonIds = get(comparisonTeamSeasonIdsToFilterByAtom);

  return pipeFilterFunctions<FormattedGame>(
    (entityGame: FormattedGameWithTeam[]) => filterGamesByGameType(entityGame, selectedGameType),
    (entityGame: FormattedGameWithTeam[]) => filterGamesByCompetition(entityGame, selectedCompetitionIds),
    (entityGame: FormattedGameWithTeam[]) => filterGamesBySeason(entityGame, selectedSeasonIds),
  );
});

export const comparisonTeamFilteredGamesAtom = atom(async get => {
  const { startDate, endDate, gameRange, periodRange } = get(comparisonFiltersAtom);
  return getFilteredGames(get, teamComparisonGamesAtom, comparisonTeamGameFilterFunctionsAtom, {
    startDate,
    endDate,
    gameRange,
    periodRange,
  });
});

const comparisonTeamGamesOptionsAtom = atom(async get => {
  const games = await get(comparisonTeamFilteredGamesAtom);
  return games.map(({ game: { gameId, name } }) => convertToDropdownOption(name, gameId));
});

export const comparisonTeamSelectedGamesAtom = atom(async get =>
  getSelectedGames(get, comparisonTeamGamesOptionsAtom, get(comparisonFiltersAtom).deselectedGameIds),
);

export const comparisonTeamGamesBySeasonAtom = atom(async get =>
  getGamesBySeason(
    get,
    comparisonTeamSeasonOptionsAtom,
    comparisonTeamCompetitionOptionsAtom,
    comparisonTeamFilteredGamesAtom,
  ),
);

const comparisonTeamSelectedGamesForQueryAtom = atom(async get => {
  const selectedGames = await get(comparisonTeamSelectedGamesAtom);
  const games = selectedGames.length > 0 ? selectedGames : await get(comparisonTeamGamesOptionsAtom);
  return games.map(({ value }) => value);
});

export const unwrappedComparisonTeamSelectedGamesForQueryAtom = unwrap(
  comparisonTeamSelectedGamesForQueryAtom,
  prev => prev || [],
);
